import { Container, Graphics } from 'pixi.js';
import { COLORS } from '@/config/colors';
import type { I18nKey } from '@/i18n';
import { crossGlyph } from './hangar-icons';

/*
 * PAUSE → MAIN MENU pictures (polish 1, docs/06): the house glyph and the
 * confirm's art — a big house plus a badge that says what happens to the
 * run, readable without words (4-year-old rule): green ✓ = saved, yellow
 * ↺ = back to the last save, red ✕ = nothing saved yet.
 */

/** What going home does to the run (run/home-flow.ts decides). */
export type HomeMode = 'saved' | 'rewind' | 'lost';

const HOUSE_S = 30;
const BADGE_R = 15;
const INK = 0x0f0c1a;
const REWIND_COLOR = COLORS.playerYellow;

/** House: roof triangle + body with a door, centred on (x, y), half-size s. */
export function houseGlyph(g: Graphics, x: number, y: number, s: number, color: number): Graphics {
  g.poly([x - s, y - s * 0.05, x, y - s, x + s, y - s * 0.05]).fill(color);
  g.rect(x - s * 0.72, y - s * 0.1, s * 1.44, s * 1.05).fill(color);
  g.roundRect(x - s * 0.2, y + s * 0.38, s * 0.4, s * 0.57, s * 0.08).fill(INK);
  return g;
}

/** Check mark centred on (x, y), half-size s. */
export function checkGlyph(g: Graphics, x: number, y: number, s: number, color: number): Graphics {
  return g.moveTo(x - s, y).lineTo(x - s * 0.3, y + s * 0.7).lineTo(x + s, y - s * 0.8)
    .stroke({ color, width: Math.max(3, s * 0.38), cap: 'round', join: 'round' });
}

/** Counter-clockwise arrow ↺ ("goes back"), centred on (x, y). */
function rewindGlyph(g: Graphics, x: number, y: number, s: number, color: number): void {
  g.moveTo(x + s * Math.cos(-0.6), y + s * Math.sin(-0.6))
    .arc(x, y, s, -0.6, Math.PI * 1.25).stroke({ color, width: Math.max(3, s * 0.3), cap: 'round' });
  const a = Math.PI * 1.25;
  const tx = x + s * Math.cos(a);
  const ty = y + s * Math.sin(a);
  g.poly([tx - s * 0.55, ty - s * 0.05, tx + s * 0.35, ty - s * 0.45, tx + s * 0.15, ty + s * 0.5]).fill(color);
}

/** i18n line under the art (secondary — the badge already says it). */
export function homeMessageKey(mode: HomeMode): I18nKey {
  return mode === 'saved' ? 'pause.homeSaved' : mode === 'rewind' ? 'pause.homeRewind' : 'pause.homeLost';
}

/** Confirm art: house + status badge; centred on x = 0, top edge at y = 0. */
export function buildHomeArt(mode: HomeMode): Container {
  const c = new Container();
  const g = new Graphics();
  houseGlyph(g, 0, HOUSE_S, HOUSE_S, COLORS.uiAccent);
  const bx = HOUSE_S * 0.95;
  const by = HOUSE_S * 1.55;
  const color = mode === 'saved' ? COLORS.statUp : mode === 'rewind' ? REWIND_COLOR : COLORS.dangerRed;
  g.circle(bx, by, BADGE_R).fill(INK).stroke({ color, width: 3 });
  if (mode === 'saved') checkGlyph(g, bx, by, BADGE_R * 0.5, color);
  else if (mode === 'rewind') rewindGlyph(g, bx, by, BADGE_R * 0.5, color);
  else crossGlyph(g, bx, by, BADGE_R * 0.42, color);
  c.addChild(g);
  return c;
}
